import { CommonModule } from '@angular/common';
import { Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { MarkdownModule } from 'ngx-markdown';
import type { ContentProgressUpdate } from '../../content-progress-update';
import type { ContentPositionUpdate } from '../../content-position-update';

export const RICH_TEXT_NO_SCROLL_DELAY_MS = 15_000;
const SCROLL_END_THRESHOLD_PX = 8;

@Component({
  selector: 'l-content-rich-text',
  standalone: true,
  imports: [CommonModule, MarkdownModule],
  templateUrl: './content-rich-text.component.html',
})
export class ContentRichTextComponent implements OnInit, OnChanges, OnDestroy {
  @ViewChild('markdownContainer')
  private readonly markdownContainer?: ElementRef<HTMLElement>;

  @Input()
  public mdContent: string = '';

  @Input()
  public classId: string = '';

  /** Fração do texto (0 a 1) em que a leitura parou; nula quando ainda não há posição salva. */
  @Input()
  public resumePosition: number | null = null;

  @Output()
  public progressChange: EventEmitter<ContentProgressUpdate> = new EventEmitter<ContentProgressUpdate>();

  @Output()
  public positionChange: EventEmitter<ContentPositionUpdate> = new EventEmitter<ContentPositionUpdate>();

  private boundClassId: string = '';

  private lastReportedProgress: number = 0;

  private scrollTarget: HTMLElement | null = null;

  private resizeObserver: ResizeObserver | null = null;

  private dwellTimer: ReturnType<typeof setTimeout> | null = null;

  private restoreRatio: number | null = null;

  private restoreScrollTop: number | null = null;

  public ngOnInit(): void {
    this.boundClassId = this.classId;
    this.restoreRatio = this.resumePosition ? Math.min(1, this.resumePosition) : null;
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['classId']) {
      this.boundClassId = this.classId;
    }

    if (changes['mdContent'] && !changes['mdContent'].firstChange) {
      this.lastReportedProgress = 0;
      this.clearDwellTimer();
      this.clearScrollRestore();
    }
  }

  public ngOnDestroy(): void {
    this.teardownReadingProgress();
  }

  public onMarkdownReady(): void {
    this.enforceBlankLinks();
    this.boundClassId = this.classId;
    this.bindReadingProgress();
  }

  public enforceBlankLinks(): void {
    const container = this.markdownContainer?.nativeElement;

    if (!container) {
      return;
    }

    const anchors = container.querySelectorAll<HTMLAnchorElement>('a[href]');

    anchors.forEach((anchor) => {
      anchor.setAttribute('target', '_blank');
      anchor.setAttribute('rel', 'noopener noreferrer');
    });
  }

  public syncReadingProgress(): void {
    const scroller = this.scrollTarget ?? this.markdownContainer?.nativeElement;
    if (!scroller) return;

    const maxScroll = scroller.scrollHeight - scroller.clientHeight;

    if (maxScroll <= SCROLL_END_THRESHOLD_PX) {
      this.startDwellTimer();
      return;
    }

    this.clearDwellTimer();
    const remaining = maxScroll - scroller.scrollTop;
    const progress = remaining <= SCROLL_END_THRESHOLD_PX ? 100 : (scroller.scrollTop / maxScroll) * 100;
    this.reportProgress(progress);
  }

  private bindReadingProgress(): void {
    const article = this.markdownContainer?.nativeElement;
    if (!article) return;

    const scroller = this.findScrollParent(article) ?? article;

    if (this.scrollTarget !== scroller) {
      this.scrollTarget?.removeEventListener('scroll', this.onScroll);
      this.scrollTarget = scroller;
      scroller.addEventListener('scroll', this.onScroll, { passive: true });
    }

    this.resizeObserver?.disconnect();

    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => {
        this.applyScrollRestore();
        this.syncReadingProgress();
      });
      this.resizeObserver.observe(article);
      this.resizeObserver.observe(scroller);
    }

    this.applyScrollRestore();
    this.syncReadingProgress();
  }

  private readonly onScroll = (): void => {
    const scroller = this.scrollTarget;

    if (scroller && this.restoreScrollTop !== null && Math.abs(scroller.scrollTop - this.restoreScrollTop) > SCROLL_END_THRESHOLD_PX) {
      this.clearScrollRestore();
    }

    if (scroller && this.restoreRatio === null) {
      this.reportPosition(scroller);
    }

    this.syncReadingProgress();
  };

  /**
   * Leva a rolagem ao trecho em que a leitura parou. Reaplica enquanto o texto ainda
   * cresce (imagens, fórmulas) e para assim que o aluno rola por conta própria.
   */
  private applyScrollRestore(): void {
    const scroller = this.scrollTarget;
    if (!scroller || this.restoreRatio === null) return;

    const maxScroll = scroller.scrollHeight - scroller.clientHeight;
    if (maxScroll <= SCROLL_END_THRESHOLD_PX) return;

    const target = Math.round(this.restoreRatio * maxScroll);
    this.restoreScrollTop = target;
    scroller.scrollTop = target;
  }

  private clearScrollRestore(): void {
    this.restoreRatio = null;
    this.restoreScrollTop = null;
  }

  private reportPosition(scroller: HTMLElement): void {
    const maxScroll = scroller.scrollHeight - scroller.clientHeight;
    const ratio = maxScroll > 0 ? Math.min(1, Math.max(0, scroller.scrollTop / maxScroll)) : 0;

    this.positionChange.emit({
      classId: this.boundClassId,
      position: ratio,
    });
  }

  private startDwellTimer(): void {
    if (this.dwellTimer !== null || this.lastReportedProgress >= 100) return;

    this.dwellTimer = setTimeout(() => {
      this.dwellTimer = null;
      this.reportProgress(100);
    }, RICH_TEXT_NO_SCROLL_DELAY_MS);
  }

  private clearDwellTimer(): void {
    if (!this.dwellTimer) return;

    clearTimeout(this.dwellTimer);
    this.dwellTimer = null;
  }

  private teardownReadingProgress(): void {
    this.clearDwellTimer();
    this.scrollTarget?.removeEventListener('scroll', this.onScroll);
    this.scrollTarget = null;
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
  }

  private findScrollParent(element: HTMLElement): HTMLElement | null {
    let current = element.parentElement;

    while (current) {
      const overflowY = getComputedStyle(current).overflowY;
      if (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay') {
        return current;
      }

      current = current.parentElement;
    }

    return null;
  }

  private reportProgress(progress: number): void {
    const clamped = Math.min(100, Math.max(0, progress));
    const normalized = clamped >= 99.5 ? 100 : Math.round(clamped);

    if (normalized <= this.lastReportedProgress) return;

    this.lastReportedProgress = normalized;
    this.progressChange.emit({
      classId: this.boundClassId,
      progress: normalized,
    });
  }
}
